import { useNavigate, useParams, useSearchParams } from "react-router";
import { ChevronRight } from "lucide-react";
import { findWordById, meaningsForWord } from "../../domain/vocabulary/grouping.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useNow, useVocabularySnapshot } from "../hooks/bindings.ts";
import { meaningCountLabel } from "../view-models/labels.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import { TypeTag } from "../components/TypeTag.tsx";
import styles from "../design-system/ui.module.css";

export function SavedMeaningsScreen() {
  const { wordId = "" } = useParams();
  const [params] = useSearchParams();
  const fromAdd = params.get("from") === "add";
  const snapshot = useVocabularySnapshot();
  const { generation, commands, queries } = useAppServices();
  const now = useNow();
  const navigate = useNavigate();
  const word = findWordById(snapshot, wordId);
  const meanings = meaningsForWord(snapshot, wordId);

  if (!word || meanings.length === 0) {
    return (
      <Frame>
        <Screen withNav={false}>
          <TopBar title="Saved Meanings" backTo={fromAdd ? "/add" : "/words"} />
          <div className={styles.empty}>This word is no longer saved.</div>
        </Screen>
      </Frame>
    );
  }

  const types = [...new Set(meanings.map((meaning) => meaning.type))];

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar title={fromAdd ? "Add New Word" : "Saved Meanings"} backTo={fromAdd ? "/add" : "/words"} />
        <h1 className={styles.heroWord}>{word.displayText}</h1>
        <div className={styles.tags}>
          {types.map((type) => (
            <TypeTag key={type} type={type} />
          ))}
        </div>
        {fromAdd ? (
          <div className={styles.banner}>
            <div>
              <strong>
                You already have {meaningCountLabel(meanings.length)} of “{word.displayText}”.
              </strong>
              <br />
              Open one below, or add another meaning.
            </div>
          </div>
        ) : (
          <p className={styles.sub} style={{ margin: "15px 0 22px" }}>
            {meaningCountLabel(meanings.length)}. Which one would you like to open?
          </p>
        )}
        {meanings.map((meaning) => (
          <button
            key={meaning.id}
            className={styles.sense}
            type="button"
            onClick={() => navigate(`/meanings/${meaning.id}`)}
          >
            <div>
              <TypeTag type={meaning.type} />
              <h3>{meaning.shortLabel}</h3>
              <p>{meaning.example}</p>
              <div className={styles.practiceStatus} style={{ marginTop: 9 }}>
                <p>{queries.scheduleLines(meaning.id, now).written}</p>
                <p>{queries.scheduleLines(meaning.id, now).hideMeaning}</p>
                <p>{queries.scheduleLines(meaning.id, now).hideWord}</p>
              </div>
            </div>
            <ChevronRight className={styles.chevron} />
          </button>
        ))}
        <div className={styles.footerAction}>
          <PrimaryButton
            secondary
            onClick={() => {
              generation.addAnotherMeaning(word.id);
              navigate("/add/choose");
            }}
          >
            + Add another meaning
          </PrimaryButton>
        </div>
        <button
          className={styles.textButton}
          type="button"
          onClick={async () => {
            if (window.confirm(`Delete “${word.displayText}” and all its meanings?`)) {
              await commands.deleteWord(word.id);
              navigate(fromAdd ? "/add" : "/words");
            }
          }}
        >
          Delete word
        </button>
      </Screen>
    </Frame>
  );
}
